/**
 * Enhanced Insights Data with Infographic Content
 */

export const ENHANCED_INSIGHTS = [
  {
    id: 1,
    title: "Complete GCC Setup Guide 2024",
    description: "Comprehensive framework covering every aspect of Global Capability Center establishment, from initial strategy to operational excellence in 90 days.",
    type: "guide",
    image: "https://images.unsplash.com/photo-1507003211169-0a1dd7228f2d?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
    author: {
      name: "Sanjay Jain",
      role: "Chief Innovation Officer",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80"
    },
    readTime: "45 min",
    relevantFor: ["C-Suite", "Strategy"],
    featured: true,
    infographicSections: [
      {
        icon: "Target",
        title: "Strategic Foundation Phase",
        description: "Establish clear objectives, governance structure, and success metrics",
        keyPoints: [
          "Define business case with ROI projections",
          "Establish governance and decision-making framework",
          "Set up legal entity and compliance requirements",
          "Identify key stakeholders and communication plan"
        ],
        metrics: [
          { value: "21 Days", label: "Setup Time" },
          { value: "15%", label: "Cost Savings" },
          { value: "95%", label: "Success Rate" },
          { value: "30+", label: "Checkpoints" }
        ]
      },
      {
        icon: "Users",
        title: "Talent Acquisition & Team Building",
        description: "Recruit, onboard, and develop high-performing teams",
        keyPoints: [
          "Multi-stage SkillStack™ talent sourcing with strict acceptance criteria",
          "Structured interview process and assessment",
          "Comprehensive onboarding and cultural integration",
          "Performance management and career development"
        ],
        metrics: [
          { value: "30 Days", label: "Hire Time" },
          { value: "95%", label: "Retention Rate" },
          { value: "500+", label: "Vetted Profiles" },
          { value: "40%", label: "Cost Reduction" }
        ]
      },
      {
        icon: "Cog",
        title: "Operational Excellence Setup",
        description: "Implement processes, technology, and infrastructure for optimal performance",
        keyPoints: [
          "Technology stack selection and implementation",
          "Process standardization and automation",
          "Quality management systems",
          "Security and compliance frameworks"
        ],
        metrics: [
          { value: "48 Hours", label: "Infrastructure" },
          { value: "99.9%", label: "Uptime SLA" },
          { value: "ISO 27001", label: "Security" },
          { value: "24/7", label: "Support" }
        ]
      }
    ]
  },
  {
    id: 2,
    title: "Q4 2024 GCC Market Intelligence Report",
    description: "Quarterly analysis of market trends, salary benchmarks, and regulatory updates affecting GCC operations across major Indian cities.",
    type: "report",
    image: "https://images.unsplash.com/photo-1551288049-bebda4e38f71?ixlib=rb-4.0.3&ixid=M3wxMJA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
    author: {
      name: "Sanjay Jain",
      role: "Chief Innovation Officer",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80"
    },
    readTime: "25 min",
    relevantFor: ["Procurement", "Strategy"],
    featured: false,
    infographicSections: [
      {
        icon: "TrendingUp",
        title: "Market Growth Trends",
        description: "Current market dynamics and growth projections for GCC industry in India",
        keyPoints: [
          "20% YoY growth in GCC establishments",
          "Bangalore, Hyderabad lead in new setups",
          "AI/ML roles demand increased by 45%",
          "Remote-first models gaining traction"
        ],
        metrics: [
          { value: "1.5M+", label: "GCC Jobs" },
          { value: "20%", label: "Growth Rate" },
          { value: "$50B", label: "Market Size" },
          { value: "1,400+", label: "Active GCCs" }
        ]
      },
      {
        icon: "DollarSign",
        title: "Salary Benchmarks",
        description: "Latest compensation trends across key technology roles",
        keyPoints: [
          "Senior developers: ₹15-25L average",
          "Data scientists: ₹18-30L range",
          "DevOps engineers: ₹12-22L typical",
          "AI specialists: ₹25-40L premium"
        ],
        metrics: [
          { value: "12%", label: "Salary Increase" },
          { value: "₹18L", label: "Average CTC" },
          { value: "85%", label: "Satisfied Rate" },
          { value: "6 Months", label: "Avg Tenure" }
        ]
      }
    ]
  },
  {
    id: 3,
    title: "Talent Acquisition Playbook for Indian GCCs",
    description: "Strategic framework for building high-performance teams, including recruitment strategies, interview processes, and retention tactics.",
    type: "guide",
    image: "https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2069&q=80",
    author: {
      name: "Sanjay Jain",
      role: "Chief Innovation Officer",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80"
    },
    readTime: "35 min",
    relevantFor: ["HR", "Operations"],
    featured: false,
    infographicSections: [
      {
        icon: "Search",
        title: "Talent Sourcing Strategy",
        description: "Multi-channel approach to identify and attract top talent",
        keyPoints: [
          "Elite university partnerships for fresh talent",
          "Industry network leveraging for experienced hires",
          "Technical assessment and cultural fit evaluation",
          "Competitive compensation and benefits package"
        ],
        metrics: [
          { value: "2%", label: "Acceptance Rate" },
          { value: "15 Days", label: "Avg Hiring" },
          { value: "95%", label: "Retention" },
          { value: "4.8/5", label: "Satisfaction" }
        ]
      },
      {
        icon: "UserCheck",
        title: "Interview & Assessment Process",
        description: "Comprehensive evaluation framework ensuring quality hires",
        keyPoints: [
          "Technical coding assessments",
          "System design and architecture reviews",
          "Behavioral and cultural fit interviews",
          "Reference checks and background verification"
        ],
        metrics: [
          { value: "3 Rounds", label: "Interview Stages" },
          { value: "4 Hours", label: "Total Time" },
          { value: "90%", label: "Offer Rate" },
          { value: "48 Hours", label: "Decision Time" }
        ]
      }
    ]
  },
  {
    id: 4,
    title: "Regulatory Compliance Checklist 2024",
    description: "Essential compliance requirements for GCC operations, including labor laws, tax obligations, and regulatory reporting standards.",
    type: "checklist",
    image: "https://images.unsplash.com/photo-1589829545856-d10d557cf95f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2070&q=80",
    author: {
      name: "Sanjay Jain",
      role: "Chief Innovation Officer",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80"
    },
    readTime: "15 min",
    relevantFor: ["Legal", "Compliance"],
    featured: false,
    infographicSections: [
      {
        icon: "Shield",
        title: "Legal Entity Setup",
        description: "Company registration and legal framework establishment",
        keyPoints: [
          "Private Limited Company incorporation",
          "Foreign Investment approval (FEMA)",
          "Director KYC and board resolutions",
          "Statutory registers and compliance calendar"
        ],
        metrics: [
          { value: "14 Days", label: "Setup Time" },
          { value: "100%", label: "Compliance" },
          { value: "15+", label: "Licenses" },
          { value: "₹2L", label: "Setup Cost" }
        ]
      },
      {
        icon: "FileText",
        title: "Ongoing Compliance",
        description: "Regular statutory and regulatory requirements",
        keyPoints: [
          "Monthly GST returns and TDS compliance",
          "Annual ROC filings and board meetings",
          "Employee PF, ESI, and statutory benefits",
          "Transfer pricing documentation"
        ],
        metrics: [
          { value: "Monthly", label: "GST Returns" },
          { value: "Annual", label: "ROC Filing" },
          { value: "Quarterly", label: "TDS Returns" },
          { value: "24/7", label: "Support" }
        ]
      }
    ]
  },
  {
    id: 5,
    title: "Cost Optimization Strategies Whitepaper",
    description: "Advanced techniques for reducing operational costs while maintaining service quality and employee satisfaction in mature GCCs.",
    type: "whitepaper",
    image: "https://images.unsplash.com/photo-1460925895917-afdab827c52f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2015&q=80",
    author: {
      name: "Sanjay Jain",
      role: "Chief Innovation Officer",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80"
    },
    readTime: "28 min",
    relevantFor: ["Finance", "Operations"],
    featured: false,
    infographicSections: [
      {
        icon: "TrendingDown",
        title: "Infrastructure Optimization",
        description: "Smart approaches to reduce operational overhead costs",
        keyPoints: [
          "Cloud-first architecture adoption",
          "Automation of routine operational tasks",
          "Energy-efficient office space design",
          "Vendor consolidation and negotiation"
        ],
        metrics: [
          { value: "30%", label: "Infrastructure Savings" },
          { value: "50%", label: "Automation Rate" },
          { value: "25%", label: "Energy Reduction" },
          { value: "40%", label: "Vendor Savings" }
        ]
      },
      {
        icon: "Users",
        title: "Workforce Optimization",
        description: "Balancing cost efficiency with talent retention",
        keyPoints: [
          "Performance-based compensation models",
          "Cross-training and skill development",
          "Remote work and flexible arrangements",
          "Employee wellness and retention programs"
        ],
        metrics: [
          { value: "15%", label: "Cost Per Employee" },
          { value: "95%", label: "Retention Rate" },
          { value: "80%", label: "Satisfaction" },
          { value: "20%", label: "Productivity Gain" }
        ]
      }
    ]
  },
  {
    id: 6,
    title: "Technology Integration Best Practices",
    description: "Modern approaches to technology stack selection, implementation, and optimization for GCC operations and digital transformation.",
    type: "case-study",
    image: "https://images.unsplash.com/photo-1518186285589-2f7649de83e0?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=2074&q=80",
    author: {
      name: "Sanjay Jain",
      role: "Chief Innovation Officer",
      avatar: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=150&q=80"
    },
    readTime: "32 min",
    relevantFor: ["Technology", "Operations"],
    featured: false,
    infographicSections: [
      {
        icon: "Layers",
        title: "Technology Stack Selection",
        description: "Strategic approach to choosing the right technology foundation",
        keyPoints: [
          "Cloud-native architecture principles",
          "Microservices and containerization",
          "AI/ML platform integration",
          "Security-first design approach"
        ],
        metrics: [
          { value: "99.9%", label: "Uptime SLA" },
          { value: "50%", label: "Faster Deployment" },
          { value: "30%", label: "Cost Reduction" },
          { value: "ISO 27001", label: "Security Cert" }
        ]
      },
      {
        icon: "Zap",
        title: "Innovation Lab Integration",
        description: "Leveraging AI and emerging technologies for competitive advantage",
        keyPoints: [
          "Pre-built AI agents and workflows",
          "Low-code/no-code development platforms",
          "Advanced analytics and reporting",
          "Continuous integration and deployment"
        ],
        metrics: [
          { value: "65%", label: "Faster TTM" },
          { value: "80%", label: "Dev Productivity" },
          { value: "10x", label: "Innovation Speed" },
          { value: "100+", label: "Ready Workflows" }
        ]
      }
    ]
  }
];

export default ENHANCED_INSIGHTS;